import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { useUpdateTeamBudget, useDeleteTeamBudget } from "@/queries/credit-usage-queries";
import { formatError } from "@/utils/errors";
import { TrackLocations } from "@/hooks/use-segment";
import { useCallback, useEffect, useState, type FC, type FormEvent } from "react";

type EditTeamBudgetDialogProps = {
    open: boolean;
    onClose: () => void;
    organizationId: string;
    teamId: string;
    teamName: string;
    /** Current budget. undefined = no allocation exists yet. */
    currentBudget?: number;
};

export const EditTeamBudgetDialog: FC<EditTeamBudgetDialogProps> = ({
    open,
    onClose,
    organizationId,
    teamId,
    teamName,
    currentBudget,
}) => {
    const { toast } = useToast();
    const updateBudget = useUpdateTeamBudget();
    const deleteBudget = useDeleteTeamBudget();
    const isBusy = updateBudget.isPending || deleteBudget.isPending;
    const [budgetValue, setBudgetValue] = useState(currentBudget?.toString() ?? "");

    useEffect(() => {
        if (open) {
            setBudgetValue(currentBudget?.toString() ?? "");
        }
    }, [open, currentBudget]);

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            const parsed = Number(budgetValue);
            if (!Number.isInteger(parsed) || parsed <= 0) {
                toast({ title: "Budget must be a positive whole number" });
                return;
            }

            try {
                await updateBudget.mutateAsync({
                    organizationId,
                    teamId,
                    creditBudget: parsed,
                    isNew: currentBudget === undefined,
                });

                toast({ title: `Budget for ${teamName} updated to ${parsed.toLocaleString()} credits` });
                onClose();
            } catch (error) {
                toast({ title: "Failed to update budget", description: formatError(error) });
            }
        },
        [budgetValue, organizationId, teamId, teamName, currentBudget, updateBudget, toast, onClose],
    );

    const handleRemoveBudget = useCallback(async () => {
        try {
            await deleteBudget.mutateAsync({ organizationId, teamId });
            toast({ title: `Budget removed for ${teamName}` });
            onClose();
        } catch (error) {
            toast({ title: "Failed to remove budget", description: formatError(error) });
        }
    }, [organizationId, teamId, teamName, deleteBudget, toast, onClose]);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-track-location={TrackLocations.EditTeamBudgetDialog}>
                <DialogHeader>
                    <DialogTitle>Edit Credit Budget</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <DialogBody>
                        <Text className="mb-4 text-sm text-content-secondary">
                            Set the credit budget for <strong>{teamName}</strong>. Budgets are soft limits for
                            reporting — they are not enforced at usage time.
                        </Text>
                        <div className="flex flex-col gap-2">
                            <Label htmlFor="budget-input">Credit budget</Label>
                            <Input
                                id="budget-input"
                                type="number"
                                min={1}
                                step={1}
                                value={budgetValue}
                                onChange={(e) => setBudgetValue(e.target.value)}
                                placeholder="e.g. 10000"
                                autoFocus
                                aria-describedby="budget-input-hint"
                            />
                            <span id="budget-input-hint" className="text-xs text-content-tertiary">
                                Credits per billing period
                            </span>
                        </div>
                    </DialogBody>
                    <DialogFooter>
                        <div className="flex w-full items-center justify-between">
                            <div>
                                {currentBudget !== undefined && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleRemoveBudget}
                                        loading={deleteBudget.isPending}
                                        disabled={isBusy}
                                        className="text-content-destructive"
                                        data-tracking-id="team-budget-remove"
                                    >
                                        Remove budget
                                    </Button>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <DialogClose asChild>
                                    <Button type="button" variant="secondary" data-tracking-id="team-budget-cancel">
                                        Cancel
                                    </Button>
                                </DialogClose>
                                <Button type="submit" loading={updateBudget.isPending} disabled={isBusy} data-tracking-id="team-budget-save">
                                    {currentBudget !== undefined ? "Update" : "Set Budget"}
                                </Button>
                            </div>
                        </div>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
